import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import {
  buildCoursePlayerRoute,
  libraryMyItemsMyCourses
} from "../routes/LearningCenterRoutes";

import { useUserAssignments } from "./apiHooks/userAssignments";
import { useTrainingObjects } from "./apiHooks/trainingObjects";
import useUser from "./useUser";
import useModal from "./useModal";
import useScopedTranslation from "./useScopedTranslation";

import ChooseVersionModalDialog from "../components/Courses/ChooseVersionModalDialog";
import NoPermissionModalDialog from "../components/Courses/NoPermissionModalDialog";
import WarningModal from "../components/WarningModal/WarningModal";

import { useEnrollments } from "./learningCenter/myCourses";
import { assignmentTypes } from "../constants";
import { isIlt } from "../utils/course_utils";
export const ASSIGNMENT_EXISTS = "ASSIGNMENT_EXISTS";
export const TRAINING_OBJECT_EXISTS = "TRAINING_OBJECT_EXISTS";
export const NO_ACTION_NEEDED = "NO_ACTION_NEEDED";
export const NOTHING_EXISTS = "NOTHING_EXISTS";

const CreateSelfAssignmentModal = ({
  trainingObject,
  enrolledTrainingObjectId,
  onResult
}) => {
  const t = useScopedTranslation("createSelfAssignmentModal");

  const { createUserAssignment } = useUserAssignments();

  const onSubmit = async () => {
    const { data: userAssignment } = await createUserAssignment({
      trainingObjectId: trainingObject.id,
      enrolledTrainingObjectId: enrolledTrainingObjectId
    });
    onResult(userAssignment);
  };

  return (
    <WarningModal
      open={true}
      onClose={() => onResult(null)}
      onSubmit={() => onSubmit()}
      warningMessage={t("createAssignmentMessage")}
    />
  );
};

const ChooseVersionModal = ({ onResult, assignment }) => {
  const user = useUser();
  const { enrollIntoCourse } = useEnrollments();
  const [showDialog, setShowDialog] = useState(true);

  // assignment.enrolledTrainingObjectId == version.id
  // filter out non-display in library ILTs that aren't assigned

  const versions = assignment?.versions;
  const filteredVersions =
    versions && versions.length > 0
      ? versions.filter(
          version => version.parentId == assignment.assignedTrainingObjectId
        )
      : [];
  const courseVersions =
    assignment.assignmentType == assignmentTypes.self && isIlt(assignment)
      ? filteredVersions.filter(version => {
          return version.displayInLibrary;
        })
      : filteredVersions;

  useEffect(() => {
    if (courseVersions.length == 1 && !isIlt(assignment)) {
      enrollIntoCourse(
        assignment.id,
        courseVersions[0].releaseId,
        courseVersions[0].showOverview ? "overview" : "first"
      );
      setShowDialog(false);
    }
  }, []);

  const assignmentWithVersions = {
    id: assignment.id,
    ...assignment,
    versions: [...courseVersions]
  };

  return (
    showDialog && (
      <ChooseVersionModalDialog
        user={user}
        close={() => onResult(null)}
        showDialog={true}
        courseVersions={assignmentWithVersions.versions}
        assignment={assignmentWithVersions}
        enrollIntoCourse={(version, assignment) => {
          enrollIntoCourse(
            assignment.id,
            version.releaseId,
            version.showOverview ? "overview" : "first"
          );
        }}
      />
    )
  );
};

export const useAutoClickedAssignment = () => {
  const location = useLocation();
  const history = useHistory();
  const [assignment, setAssignment] = useState(null);
  const [trainingObject, setTrainingObject] = useState(null);
  const [enrolledTrainingObject, setEnrolledTrainingObject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [result, setResult] = useState(null);
  const dispatch = useDispatch();

  const { getUserAssignment } = useUserAssignments();
  const { getTrainingObject } = useTrainingObjects();

  const openChooseVersionModal = useModal(ChooseVersionModal);
  const openNoPermissionModal = useModal(NoPermissionModalDialog);
  const openCreateSelfAssignmentModal = useModal(CreateSelfAssignmentModal);

  const assignmentId = new URLSearchParams(location.search).get("assignment");

  const trainingObjectId = new URLSearchParams(location.search).get(
    "training_object"
  );

  const enrolledTrainingObjectId = new URLSearchParams(location.search).get(
    "enrolled_training_object"
  );

  useEffect(() => {
    (async () => {
      let trainingObject, assignment;

      if (assignmentId) {
        const { data } = await getUserAssignment(assignmentId, {
          include: ["versions"]
        });

        if (data) {
          assignment = data;
          assignment.versions = data.included.map(version => {
            return { id: version.id, ...version.attributes };
          });
          setAssignment({ id: assignmentId, ...data });
        }
      }

      if (trainingObjectId) {
        const { data } = await getTrainingObject(trainingObjectId);

        if (data) {
          trainingObject = data;
          setTrainingObject(data);
        }
      }

      if (enrolledTrainingObjectId) {
        const { data } = await getTrainingObject(enrolledTrainingObjectId);

        if (data) {
          setEnrolledTrainingObject(data);
        }
      }

      if (!assignmentId && !trainingObjectId) {
        setResult(NO_ACTION_NEEDED);
      } else if (assignment) {
        setResult(ASSIGNMENT_EXISTS);
      } else if (trainingObject) {
        setResult(TRAINING_OBJECT_EXISTS);
      } else {
        setResult(NOTHING_EXISTS);
      }

      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!result) {
        return;
      }

      history.replace({
        search: ""
      });

      if (result === NOTHING_EXISTS) {
        openNoPermissionModal({
          assignment: null
        });
      } else if (result === ASSIGNMENT_EXISTS) {
        if (assignment.enrolledCtoaId) {
          dispatch(
            push(buildCoursePlayerRoute(assignment.assignedTrainingObjectId))
          );
        } else {
          openChooseVersionModal({
            assignment: assignment
          });
        }
      } else if (result === TRAINING_OBJECT_EXISTS) {
        const shouldDisplayInLibrary =
          trainingObject.typeId === 1
            ? trainingObject.shouldDisplayInLibrary
            : enrolledTrainingObject.shouldDisplayInLibrary;
        if (!shouldDisplayInLibrary) {
          openNoPermissionModal({
            trainingObject
          });
        } else {
          if (enrolledTrainingObjectId) {
            assignment = await openCreateSelfAssignmentModal({
              trainingObject,
              enrolledTrainingObjectId
            }).result;
          }
          if (assignment) {
            if (assignment.enrolledCtoaId) {
              dispatch(
                push(
                  buildCoursePlayerRoute(assignment.assignedTrainingObjectId)
                )
              );
            } else {
              openChooseVersionModal({
                assignment: assignment
              });
            }
          } else {
            dispatch(push(libraryMyItemsMyCourses));
          }
        }
      }
    })();
  }, [result]);

  return { result, isLoading, assignment, trainingObject };
};
